import {Alert, Form, Select, Spin, Switch} from 'antd';
import styles from './mobileSetting.module.scss';
import {
  GET_KYC_APP_CONFIGURATION,
  UPDATE_APP_CONFIGURATION,
} from 'graphql/queries/mobileSettings';
import {useMutation, useQuery} from '@apollo/client';
import {useEffect, useState} from 'react';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';

const formItems = [
  {name: 'ssn', label: 'SSN'},
  {name: 'socialInsuranceNumber', label: 'Social Insurance Number'},
  {name: 'passport', label: 'Passport'},
  {name: 'nationalIdentityNumber', label: 'National Identity Number (NIN)'},
  {name: 'license', label: 'License'},
  {name: 'idcard', label: 'ID Card'},
];

// Kept out of `formItems` on purpose: these two live under `kyc.manualFallback`
// and must be sent nested. Spreading them into the flat toggle list would send
// them as `kyc.enabled`, which the schema rejects.
const fallbackItems = [
  {
    name: 'enabled',
    label: 'Manual verification fallback',
    description:
      'Lets users upload their ID and a selfie for manual review when Smile ID is unavailable. Submissions land in the KYC review queue.',
  },
  {
    name: 'selfieRequired',
    label: 'Require selfie',
    description:
      'Ask for a selfie alongside the document. Turn off only if compliance accepts an ID on its own.',
    // Meaningless while the fallback itself is off.
    dependsOnEnabled: true,
  },
];

// Lives one level deeper again, under kyc.manualFallback.autoSubmitToSmile, and
// is sent nested for the same reason as fallbackItems.
const autoSubmitItems = [
  {
    name: 'enabled',
    label: 'Send manual submissions to Smile ID automatically',
    description:
      'A background job hands manual submissions to Smile ID every 15 minutes, so a reviewer does not have to re-key them into the Smile portal. Each submission costs one Smile job. Admins can always send one by hand from the user KYC page whether this is on or off.',
  },
];

// Under `kyc.aml`, a sibling of manualFallback rather than a child of it: this
// governs WHEN every user is screened, automated flow included, not anything
// about the manual fallback.
const amlItems = [
  {
    name: 'onDocumentCheck',
    label: 'Run AML as soon as the document check passes',
    description:
      'Screens a user the moment their document check comes back clear. With this off, AML waits for their first deposit, so anyone who passes KYC and never funds their wallet is never screened. Each screening is billable and this covers every approved user, not only depositors — expect a large increase in volume.',
  },
  {
    name: 'deferInAfrica',
    label:
      'Keep waiting for the first deposit when both countries are in Africa',
    description:
      'A user whose residency country or document country is outside Africa is screened at document check; one with both inside Africa waits for their first deposit, as before. Turn off to screen everyone at document check.',
    // Meaningless while screening at document check is off.
    dependsOnDocumentCheck: true,
  },
];

// Which path the app takes to submit documents. Lives flat under `kyc` as
// `submissionRoute`. Advisory to the app: the backend accepts every route, so
// an app build that predates the setting keeps working on the Smile SDK.
const submissionRoutes = [
  {
    value: 'SMILE_SDK',
    label: 'Smile ID SDK',
    description:
      'The Smile SDK in the app captures and uploads straight to Smile ID. The default, and how KYC works today.',
  },
  {
    value: 'DABA_MANAGED',
    label: 'Daba managed (quality gate)',
    description:
      'The app uploads captures to our bucket; the backend checks them for blur, cropping, photocopies and expiry, and only a capture that passes is sent to Smile ID. Needs the app build that supports it.',
  },
  {
    value: 'MANUAL',
    label: 'Manual review',
    description:
      'Every user uploads for admin review, as the manual fallback does. For a Smile ID outage.',
  },
];

// Under `kyc.documentValidation`, sent nested like manualFallback. Only the
// Daba-managed route ever runs the gate; the Smile SDK and manual routes do
// not, whatever these say.
const validationModes = [
  {
    value: 'SHADOW',
    label: 'Shadow',
    description:
      'Runs the check and logs what it finds, but never blocks. Use this first, to tune the thresholds on real captures.',
  },
  {
    value: 'ENFORCE',
    label: 'Enforce',
    description:
      'A capture that fails is rejected before Smile ID and the user is asked to retake it.',
  },
];

const emptyFallback = {enabled: false, selfieRequired: false};
const emptyAutoSubmit = {enabled: false};
const emptyAml = {onDocumentCheck: false, deferInAfrica: true};
const emptyValidation = {enabled: false, mode: 'SHADOW'};
const defaultRoute = 'SMILE_SDK';

export const KycConfig = () => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();
  const [switchStates, setSwitchStates] = useState(
    formItems.reduce((acc, item) => {
      acc[item.name] = false; // Initialize all switches to false
      return acc;
    }, {}),
  );
  const [manualFallback, setManualFallback] = useState(emptyFallback);
  const [autoSubmit, setAutoSubmit] = useState(emptyAutoSubmit);
  const [aml, setAml] = useState(emptyAml);
  const [submissionRoute, setSubmissionRoute] = useState(defaultRoute);
  const [documentValidation, setDocumentValidation] = useState(emptyValidation);

  // Fetched here rather than read off the page-wide GET_APP_CONFIGURATION so a
  // field the gateway has not published yet can only break this card.
  const {
    data: configData,
    loading: loadingConfig,
    error: configError,
    refetch,
  } = useQuery(GET_KYC_APP_CONFIGURATION, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const data = configData?.getAppConfig?.config?.kyc;

  const [updateAppConfig, {loading}] = useMutation(UPDATE_APP_CONFIGURATION, {
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data) {
      const {
        // eslint-disable-next-line no-unused-vars
        __typename,
        manualFallback: storedFallback,
        aml: storedAml,
        submissionRoute: storedRoute,
        documentValidation: storedValidation,
        ...restData
      } = data;
      setSwitchStates(restData);
      setSubmissionRoute(storedRoute || defaultRoute);
      setDocumentValidation({
        enabled: Boolean(storedValidation?.enabled),
        mode: storedValidation?.mode || emptyValidation.mode,
      });
      setManualFallback({
        enabled: Boolean(storedFallback?.enabled),
        selfieRequired: Boolean(storedFallback?.selfieRequired),
      });
      setAutoSubmit({
        enabled: Boolean(storedFallback?.autoSubmitToSmile?.enabled),
      });
      setAml({
        onDocumentCheck: Boolean(storedAml?.onDocumentCheck),
        // Defaults on in the schema, so a stored config that predates it reads
        // the same way the backend does.
        deferInAfrica: storedAml?.deferInAfrica !== false,
      });
    }
  }, [data]);

  const handleSwitchChange = (name, checked) => {
    setSwitchStates(prevStates => ({
      ...prevStates,
      [name]: checked, // Update the switch state
    }));
  };

  const handleItemClick = name => {
    setSwitchStates(prevStates => ({
      ...prevStates,
      [name]: !prevStates[name], // Toggle the switch state
    }));
  };

  const handleFallbackChange = (name, checked) => {
    setManualFallback(prevStates => ({...prevStates, [name]: checked}));
  };

  const handleAutoSubmitChange = (name, checked) => {
    setAutoSubmit(prevStates => ({...prevStates, [name]: checked}));
  };

  const handleAmlChange = (name, checked) => {
    setAml(prevStates => ({...prevStates, [name]: checked}));
  };

  const isAmlItemDisabled = item =>
    Boolean(item.dependsOnDocumentCheck) && !aml.onDocumentCheck;

  const handleAmlItemClick = item => {
    if (isAmlItemDisabled(item)) return;
    setAml(prevStates => ({
      ...prevStates,
      [item.name]: !prevStates[item.name],
    }));
  };

  // Auto-submit is meaningless while the fallback itself is off: there would be
  // no manual submissions to send.
  const isAutoSubmitDisabled = !manualFallback.enabled;

  const handleAutoSubmitItemClick = item => {
    if (isAutoSubmitDisabled) return;
    setAutoSubmit(prevStates => ({
      ...prevStates,
      [item.name]: !prevStates[item.name],
    }));
  };

  const isFallbackItemDisabled = item =>
    Boolean(item.dependsOnEnabled) && !manualFallback.enabled;

  const handleFallbackItemClick = item => {
    if (isFallbackItemDisabled(item)) return;
    setManualFallback(prevStates => ({
      ...prevStates,
      [item.name]: !prevStates[item.name],
    }));
  };

  // The mode only matters while the gate is on.
  const isValidationModeDisabled = !documentValidation.enabled;
  const selectedRoute =
    submissionRoutes.find(route => route.value === submissionRoute) ||
    submissionRoutes[0];
  const selectedMode =
    validationModes.find(mode => mode.value === documentValidation.mode) ||
    validationModes[0];

  // Built field by field rather than from `switchStates` so the nested
  // `manualFallback` (and its `__typename`) can never leak into the flat part
  // of the input. `blacklistedRegions` is resent untouched — the backend now
  // merges, but a backend that still replaces would otherwise drop it.
  const buildKycInput = () => ({
    ...formItems.reduce((acc, item) => {
      acc[item.name] = Boolean(switchStates[item.name]);
      return acc;
    }, {}),
    ...(Array.isArray(switchStates.blacklistedRegions)
      ? {blacklistedRegions: switchStates.blacklistedRegions}
      : {}),
    submissionRoute,
    documentValidation: {
      enabled: documentValidation.enabled,
      mode: documentValidation.mode,
    },
    manualFallback: {
      enabled: manualFallback.enabled,
      selfieRequired: manualFallback.selfieRequired,
      // Only `enabled` is exposed here; maxAttempts / retryAfterMinutes /
      // maxPerRun keep their stored values because the backend merges this
      // sub-object rather than replacing it.
      autoSubmitToSmile: {enabled: autoSubmit.enabled},
    },
    aml: {
      onDocumentCheck: aml.onDocumentCheck,
      deferInAfrica: aml.deferInAfrica,
    },
  });

  return (
    <div className={styles.body}>
      <h2>KYC Configuration</h2>
      <p>
        This is the configuration for the KYC process. You can set the KYC
        requirements for your users.
      </p>
      {configError && (
        <Alert
          type="error"
          showIcon
          className={styles.kycConfigAlert}
          message="The KYC configuration could not be loaded"
          description={`${configError.message} — the switches below may not reflect what is stored, so do not save until this is resolved.`}
        />
      )}
      {loadingConfig && !data ? (
        <div className={styles.kycConfigLoading}>
          <Spin />
        </div>
      ) : (
        <Form
          onFinish={() => {
            setOpenModal(true);
          }}
          form={form}
          layout="horizontal"
          className={styles.row}>
          {formItems.map(item => (
            <div
              onClick={() => handleItemClick(item.name)}
              key={item.name}
              className={styles.formItem}>
              <Form.Item key={item.name} name={item.name}>
                <Switch
                  onChange={(checked, event) => {
                    event.stopPropagation();
                    handleSwitchChange(item.name, checked);
                  }}
                  checked={switchStates[item.name]}
                />
              </Form.Item>
              <div className={styles.label}>{item.label}</div>
            </div>
          ))}
          <div className={styles.fallbackCard}>
            <h3>Document Submission Route</h3>
            <p>
              Which path the app takes when a user submits their ID. The backend
              accepts every route, so switching here changes what the app does,
              not what already-submitted cards look like.
            </p>
            <div className={styles.fallbackItem}>
              <div className={styles.fallbackCopy}>
                <span className={styles.fallbackLabel}>Route</span>
                <span className={styles.fallbackDescription}>
                  {selectedRoute.description}
                </span>
              </div>
              <Select
                value={submissionRoute}
                onChange={setSubmissionRoute}
                className={styles.kycConfigSelect}
                options={submissionRoutes.map(route => ({
                  value: route.value,
                  label: route.label,
                }))}
              />
            </div>
          </div>
          <div className={styles.fallbackCard}>
            <h3>Document Quality Gate</h3>
            <p>
              The pre-flight check on the Daba-managed route: blur, cropping,
              photocopies, exposure and expiry, before a capture is sent to
              Smile ID. The Smile SDK and manual routes never run it. When the
              check service is unreachable the submission goes through
              unchecked, so it can never be the reason KYC is down.
            </p>
            <div
              onClick={() =>
                setDocumentValidation(prev => ({
                  ...prev,
                  enabled: !prev.enabled,
                }))
              }
              className={styles.fallbackItem}>
              <div className={styles.fallbackCopy}>
                <span className={styles.fallbackLabel}>
                  Check captures before Smile ID
                </span>
                <span className={styles.fallbackDescription}>
                  Off means the Daba-managed route submits straight to Smile ID
                  with no check.
                </span>
              </div>
              <Switch
                onChange={(checked, event) => {
                  event.stopPropagation();
                  setDocumentValidation(prev => ({...prev, enabled: checked}));
                }}
                checked={documentValidation.enabled}
              />
            </div>
            <div
              className={`${styles.fallbackItem} ${
                isValidationModeDisabled ? styles.fallbackItemDisabled : ''
              }`}>
              <div className={styles.fallbackCopy}>
                <span className={styles.fallbackLabel}>Mode</span>
                <span className={styles.fallbackDescription}>
                  {selectedMode.description}
                </span>
              </div>
              <Select
                disabled={isValidationModeDisabled}
                value={documentValidation.mode}
                onChange={mode =>
                  setDocumentValidation(prev => ({...prev, mode}))
                }
                className={styles.kycConfigSelect}
                options={validationModes.map(mode => ({
                  value: mode.value,
                  label: mode.label,
                }))}
              />
            </div>
          </div>
          <div className={styles.fallbackCard}>
            <h3>Manual Verification Fallback</h3>
            <p>
              What users can do when Smile ID cannot verify them. Turning the
              fallback off hides the option in the app and rejects new
              submissions; cards already waiting for review are unaffected.
            </p>
            {fallbackItems.map(item => {
              const disabled = isFallbackItemDisabled(item);
              return (
                <div
                  onClick={() => handleFallbackItemClick(item)}
                  key={item.name}
                  className={`${styles.fallbackItem} ${
                    disabled ? styles.fallbackItemDisabled : ''
                  }`}>
                  <div className={styles.fallbackCopy}>
                    <span className={styles.fallbackLabel}>{item.label}</span>
                    <span className={styles.fallbackDescription}>
                      {item.description}
                    </span>
                  </div>
                  <Switch
                    disabled={disabled}
                    onChange={(checked, event) => {
                      event.stopPropagation();
                      handleFallbackChange(item.name, checked);
                    }}
                    checked={manualFallback[item.name]}
                  />
                </div>
              );
            })}
            {autoSubmitItems.map(item => (
              <div
                onClick={() => handleAutoSubmitItemClick(item)}
                key={`autoSubmit-${item.name}`}
                className={`${styles.fallbackItem} ${
                  isAutoSubmitDisabled ? styles.fallbackItemDisabled : ''
                }`}>
                <div className={styles.fallbackCopy}>
                  <span className={styles.fallbackLabel}>{item.label}</span>
                  <span className={styles.fallbackDescription}>
                    {item.description}
                  </span>
                </div>
                <Switch
                  disabled={isAutoSubmitDisabled}
                  onChange={(checked, event) => {
                    event.stopPropagation();
                    handleAutoSubmitChange(item.name, checked);
                  }}
                  checked={autoSubmit[item.name]}
                />
              </div>
            ))}
          </div>
          <div className={styles.fallbackCard}>
            <h3>AML Screening</h3>
            <p>
              When a user is screened against sanctions and watchlists. This
              applies to the automated Smile ID flow and the manual fallback
              alike.
            </p>
            {amlItems.map(item => {
              const disabled = isAmlItemDisabled(item);
              return (
                <div
                  onClick={() => handleAmlItemClick(item)}
                  key={`aml-${item.name}`}
                  className={`${styles.fallbackItem} ${
                    disabled ? styles.fallbackItemDisabled : ''
                  }`}>
                  <div className={styles.fallbackCopy}>
                    <span className={styles.fallbackLabel}>{item.label}</span>
                    <span className={styles.fallbackDescription}>
                      {item.description}
                    </span>
                  </div>
                  <Switch
                    disabled={disabled}
                    onChange={(checked, event) => {
                      event.stopPropagation();
                      handleAmlChange(item.name, checked);
                    }}
                    checked={aml[item.name]}
                  />
                </div>
              );
            })}
          </div>
          <SubmitButton label="Proceed to Update" />
        </Form>
      )}
      <ConfirmModal
        openModal={openModal}
        setOpenModal={() => setOpenModal(false)}
        loading={loading}
        title="Update Configuration"
        confirmText="Are you sure you want to update this, This action cannot be undone?"
        handleOperation={() =>
          updateAppConfig({
            variables: {
              data: {
                kyc: buildKycInput(),
              },
            },
          })
            .then(({data: {updateAppConfig}}) => {
              if (updateAppConfig.__typename === 'Error') {
                toast.error(updateAppConfig.message);
              } else {
                toast.success('Updated Successfully');
                refetch();
                setOpenModal(false);
              }
            })
            .catch(error => toast.error(error))
        }
      />
    </div>
  );
};
